import React, { useEffect, useState, useRef, Fragment } from "react";
import { styled } from '@mui/material/styles';
import { Button } from "@mui/material";
import ResultCard from "./ResultCard";
import Sort from "./Sort";
import { connect } from "react-redux";
import {
  getResults,
  setResults,
  setFilteredResults,
  setRenderedResults,
  setSearchText,
} from "../../actions/results";
import { clearFilters } from "../../actions/filters";
import InfiniteScroll from "react-infinite-scroller";
import { initialRender } from "../../reducers/results";
import Chip from "@mui/material/Chip";
import earthLoader from "../../../assets/gifs/loader-earth.gif";
import PropTypes from "prop-types";
import { incrementResults } from "../../constants";

const PREFIX = 'Results';

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  text: `${PREFIX}-text`,
  title: `${PREFIX}-title`,
  available: `${PREFIX}-available`,
  results: `${PREFIX}-results`,
  loaderDisplay: `${PREFIX}-loaderDisplay`,
  loaderGif: `${PREFIX}-loaderGif`,
  loaderText: `${PREFIX}-loaderText`,
  loader: `${PREFIX}-loader`,
  scrollContainer: `${PREFIX}-scrollContainer`,
  scroll: `${PREFIX}-scroll`,
  searchText: `${PREFIX}-searchText`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    maxWidth: "100%",
    minWidth: "75%",
    minHeight: "90vh",
  },

  [`& .${classes.header}`]: {
    display: "flex",
    justifyContent: "space-between",
    height: "4em",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      height: "auto",
      alignItems: "baseline",
    },
  },

  [`& .${classes.text}`]: {
    display: "grid",
  },

  [`& .${classes.title}`]: {
    fontWeight: "bold",
    marginBottom: "0.5em",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  [`& .${classes.available}`]: {
    color: "#4c4c4c",
  },

  [`& .${classes.results}`]: {
    display: "flex",
    flexFlow: "wrap",
    marginLeft: "-0.5em",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
  },

  [`& .${classes.loaderDisplay}`]: {
    display: "flex",
    flexDirection: "column",
    margin: "1em auto",
    zIndex: 100,
    background: "#fff",
    borderRadius: "5px",
    overflow: "hidden",
  },

  [`& .${classes.loaderGif}`]: {
    height: "15em",
    display: "flex",
    margin: "-5rem auto",
  },

  [`& .${classes.loaderText}`]: {
    display: "flex",
    justifyContent: "center",
    borderBottom: "4px solid #0078a9",
    padding: "4px",
    width: "160px",
    margin: "1em auto",
    textAlign: "center",
  },

  [`& .${classes.loader}`]: {
    height: "2em",
    width: "100%",
    bottom: "100em",
    position: "relative",
    background: "black",
    display: "none",
  },

  [`& .${classes.scrollContainer}`]: {
    position: "fixed",
    display: "flex",
    zIndex: 10000,
    width: "100%",
    left: 0,
  },

  [`& .${classes.scroll}`]: {
	color: "rgba(0, 0, 0, 0.87)",
    background: "#fff",
    boxShadow: "0 0 5px rgba(0,0,0,0.2)",
    borderRadius: "50px",
    margin: "0 auto",
    "&:hover": {
      background: "#f9f9f9",
      boxShadow: "1px 1px 4px #a1a1a1",
    },
  },

  [`& .${classes.searchText}`]: {
    marginTop: "0.3em",
  }
}));

const Results = ({
  filteredResults,
  renderedResults,
  getResults,
  setRenderedResults,
  busy,
  errorResults,
  clearFilters,
  searchText,
  setSearchText,
}) => {

  const loader = useRef();
  const loaderText = useRef();
  const loaderGif = useRef();
  const resultsRef = useRef();

  const renderIncrement = incrementResults;
  const [renderStart, setRenderStart] = useState(0);
  const [renderEnd, setRenderEnd] = useState(renderIncrement);
  const [loadScrollComponent, setLoadScrollComponent] = useState(
    window.scrollY > window.screen.availHeight ? true : false
  );
  const loadScrollRef = useRef(loadScrollComponent);

  const loadFunc = () => {
    if (filteredResults.length && renderEnd < filteredResults.length) {
      const newResults = filteredResults.slice(
        renderStart + renderIncrement,
        renderEnd + renderIncrement
      );
      setRenderedResults([...renderedResults, ...newResults]);
      setRenderStart(renderStart + renderIncrement);
      setRenderEnd(renderEnd + renderIncrement);
    }
  };

  const handleScroll = () => {
    const load = window.scrollY > window.screen.availHeight ? true : false;
    if (load !== loadScrollRef.current) {
      setLoadScrollComponent(load);
      loadScrollRef.current = load;
    }
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const clearSearch = () => {
    getResults();
    setSearchText("");
  };

  useEffect(() => {
    (async () => {
      // get results if not in store
      if (Object.keys(filteredResults).length < 1) {
        const status = await getResults();
        if (status !== 200) {
          console.error("GET results timeout");
          loaderText.current &&
            (loaderText.current.innerHTML = "Unable to load results");
          loaderGif.current && (loaderGif.current.style.display = "none");
        }
      }
      // revert rendered results to inital length if more have been loaded
      if (Object.keys(renderedResults).length > initialRender) {
        try {
          setRenderedResults(filteredResults.slice(0, initialRender));
        } catch (error) {
          console.error("Unable to reset filtered results");
        }
      }
    })();
    window.addEventListener("scroll", handleScroll);
  }, []);

  return (
    <Root ref={resultsRef} id="results" className={classes.root}>
      {loadScrollComponent ? (
        <div className={classes.scrollContainer}>
          <Button
            id="return-to-top"
            className={classes.scroll}
            onClick={scrollToTop}
          >
            return to top
          </Button>
        </div>
      ) : (
        <Fragment />
      )}

      <div className={classes.header}>
        <div className={classes.text}>
          <span className={classes.title}>
            Results
            {searchText.length > 0 ? (
              <Chip
                label={searchText}
                onDelete={clearSearch}
                className={classes.searchText}
              />
            ) : (
              ""
            )}
          </span>
          <span className={classes.available}>
            {!busy && filteredResults.length
              ? filteredResults.length > 1
                ? `${filteredResults.length} results `
                : "1 result "
              : "0 results "}
            available for your search
          </span>
        </div>

        <Sort />
      </div>
      {busy ? (
        <Fragment>
          <div className={classes.loaderDisplay}>
            <img
              ref={loaderGif}
              src={earthLoader}
              alt="loading"
              className={classes.loaderGif}
            />
            <p id="loader-text" ref={loaderText} className={classes.loaderText}>
              loading...
            </p>
          </div>
        </Fragment>
      ) : (
        <Fragment>
          <InfiniteScroll
            hasMore={true || false}
            pageStart={0}
            initialLoad={false}
            loadMore={loadFunc}
            threshold={500}
          >
            <div className={classes.results}>
              {Object.keys(renderedResults).length > 0
                ? Object.keys(renderedResults).map((key) => {
                    const {
                      id,
                      name,
                      hazard,
                      analysisType,
                      type,
                      date,
                      scale,
                      image,
                    } = renderedResults[key];
                    return (
                      <ResultCard
                        id={id}
                        name={name}
                        hazard={hazard}
                        analysisType={analysisType}
                        type={type}
                        date={date}
                        scale={scale}
                        image={image}
                      />
                    );
                  })
                : ""}
            </div>
            <div className={classes.loader} ref={loader} />
          </InfiniteScroll>
          {renderedResults.length >= filteredResults.length ? (
            <p className={classes.loaderText}>End of results</p>
          ) : errorResults ? (
            <p className={classes.loaderText}>Unable to load results</p>
          ) : (
            <div className={classes.loaderDisplay}>
              <img
                ref={loaderGif}
                src={earthLoader}
                alt="loading"
                className={classes.loaderGif}
              />
              <p
                id="loader-text"
                ref={loaderText}
                className={classes.loaderText}
              >
                loading...
              </p>
            </div>
          )}
        </Fragment>
      )}
    </Root>
  );
};

Results.propTypes = {
  getResults: PropTypes.func.isRequired,
  setResults: PropTypes.func.isRequired,
  setFilteredResults: PropTypes.func.isRequired,
  setRenderedResults: PropTypes.func.isRequired,
  setSearchText: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  filteredResults: state.results.filtered,
  renderedResults: state.results.rendered,
  errorResults: state.results.errorResults,
  busy: state.results.busy,
  searchText: state.results.searchText,
});

export default connect(mapStateToProps, {
  getResults,
  setResults,
  setFilteredResults,
  setRenderedResults,
  setSearchText,
  clearFilters,
})(Results);
